// Tiny capture helper that fires events to PostHog without forcing
// posthog-js into the initial JS bundle. The provider loads posthog-js on
// idle and parks the singleton on `window.__posthog`. Until that happens,
// captures are queued and flushed once the singleton is available.

type PostHogClient = { capture: (event: string, props?: Record<string, unknown>) => void };

declare global {
  interface Window {
    __posthog?: PostHogClient;
    __posthogQueue?: Array<[string, Record<string, unknown> | undefined]>;
  }
}

/**
 * Capture a PostHog event without depending on posthog-js at the call site.
 * If the SDK has not finished loading yet, the event is queued and replayed
 * once `flushPostHogQueue()` runs.
 */
export function capturePostHogEvent(
  event: string,
  props?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  const ph = window.__posthog;
  if (ph) {
    ph.capture(event, props);
    return;
  }
  (window.__posthogQueue ??= []).push([event, props]);
}

export function flushPostHogQueue(client: PostHogClient): void {
  if (typeof window === 'undefined') return;
  window.__posthog = client;
  const queued = window.__posthogQueue ?? [];
  window.__posthogQueue = [];
  for (const [event, props] of queued) client.capture(event, props);
}
